import { compareNumbersAsc, compareObjects, stringToLocalDate } from "@hex-insights/core";
import { BillingAdjustment, ContractInstallment, PaymentLineItem } from "../../../Utilities";
import * as ModelUtilities from "../_General";
import * as InvoiceUtilities from "../Invoice";

export function getPaymentStatus(
	contractInstallment: Pick<ContractInstallment, "id" | "dueDate" | "portion"> & {
		invoices: {
			paymentLineItems: Pick<PaymentLineItem, "amount">[];
			billingAdjustments: Pick<BillingAdjustment, "amount">[];
		}[];
	},
	installmentAmount: number,
	amountPaid?: number,
) {
	amountPaid = amountPaid ?? calculateTotalPaid(contractInstallment);
	const amountDue = Math.max(installmentAmount - amountPaid, 0);
	return getPaymentStatusFromValues(amountDue, contractInstallment.dueDate);
}

export function getPaymentStatusFromValues(amountDue: number, dueDate: ContractInstallment["dueDate"]) {
	return ModelUtilities.getPaymentStatusFromValues(amountDue, dueDate);
}

export function calculateTotalPaid(contractInstallment: {
	invoices: {
		paymentLineItems: Pick<PaymentLineItem, "amount">[];
		billingAdjustments: Pick<BillingAdjustment, "amount">[];
	}[];
}) {
	let total = 0;
	for (let i = 0; i < contractInstallment.invoices.length; i++) {
		const invoice = contractInstallment.invoices[i];
		total += InvoiceUtilities.calculateTotalPaid(invoice);
	}
	return total;
}

export type PrecomputedContractInstallment = {
	index: number;
	totalInstallments: number;
	totalAmount: number;
	totalPaid: number;
	amountDue: number;
	paymentStatus: ModelUtilities.PaymentStatus;
};

/**
 * Groups contract installments by payment status and sorts each group by due date.
 */
export function groupContractInstallmentsByPaymentStatus<
	T extends Pick<ContractInstallment, "dueDate"> & Pick<PrecomputedContractInstallment, "paymentStatus">,
>(contractInstallments: T[]) {
	const sections: Record<ModelUtilities.PaymentStatus, T[]> = {
		[ModelUtilities.PaymentStatus.Paid]: [],
		[ModelUtilities.PaymentStatus.Overdue]: [],
		[ModelUtilities.PaymentStatus.Due]: [],
		[ModelUtilities.PaymentStatus.Upcoming]: [],
	};

	for (let i = 0; i < contractInstallments.length; i++) {
		const installment = contractInstallments[i];
		sections[installment.paymentStatus].push(installment);
	}

	// TODO hex-core-update datetime comparison
	const compare = compareObjects<T>((e) => stringToLocalDate(e.dueDate, "day").getTime(), compareNumbersAsc());
	sections[ModelUtilities.PaymentStatus.Paid].sort(compare);
	sections[ModelUtilities.PaymentStatus.Overdue].sort(compare);
	sections[ModelUtilities.PaymentStatus.Due].sort(compare);
	sections[ModelUtilities.PaymentStatus.Upcoming].sort(compare);

	return sections;
}
